'use client'

import {useMessages, useTranslations as useNextIntlTranslations} from 'next-intl'
import {useI18n} from '../components/providers/I18nProvider'
import type {CurrencyFormatOptions, LocaleCode, LocaleConfig, NumberFormatOptions, TranslationParams} from '../types/i18n/core'
import type {TranslationNamespace} from '../types/i18n/translation-keys'
import type {DateTimeFormatter} from '@fitro/date-time'

type TypedTranslationFunction = (
    key: string,
    params?: TranslationParams | string,
    maybeFallback?: string
) => string

type ResolvedTranslationKeys = {
    requestedKey: string
    relativeKey: string
    fullKey: string
}

type NextIntlTranslate = (key: string, values?: TranslationParams) => string

interface UseTranslationReturn {
    t: TypedTranslationFunction

    locale: LocaleCode
    changeLocale: (locale: LocaleCode) => Promise<void>
    isLocaleSupported: (locale: string) => locale is LocaleCode
    getLocaleConfig: (locale: LocaleCode) => LocaleConfig | undefined

    dateTime: DateTimeFormatter
    formatNumber: (num: number, options?: NumberFormatOptions) => string
    formatCurrency: (amount: number, options?: CurrencyFormatOptions) => string
    formatRelativeTime: (date: Date) => string

    hasTranslation: (key: string) => boolean
    getTranslationWithFallback: (key: string, fallback: string, params?: TranslationParams) => string

    isLoading?: boolean
}

const resolveTranslationKeys = (
    key: string,
    namespace?: TranslationNamespace
): ResolvedTranslationKeys => {
    if (!namespace) {
        return {requestedKey: key, relativeKey: key, fullKey: key}
    }

    const namespacePrefix = `${namespace}.`
    if (key.startsWith(namespacePrefix)) {
        return {
            requestedKey: key,
            relativeKey: key.slice(namespacePrefix.length),
            fullKey: key
        }
    }

    return {
        requestedKey: key,
        relativeKey: key,
        fullKey: `${namespace}.${key}`
    }
}

const isInvalidFromNextIntl = (
    key: string,
    translated: string,
    namespace?: TranslationNamespace
): boolean => {
    const {requestedKey, relativeKey, fullKey} = resolveTranslationKeys(key, namespace)

    return translated === requestedKey
        || translated === relativeKey
        || translated === fullKey
        || translated.startsWith('[Error:')
}

const getMessageAtPath = (messages: unknown, fullKey: string): string | undefined => {
    const segments = fullKey.split('.')
    let current = messages

    for (const segment of segments) {
        if (!current || typeof current !== 'object' || !(segment in current)) {
            return undefined
        }
        current = (current as Record<string, unknown>)[segment]
    }

    return typeof current === 'string' ? current : undefined
}

const interpolateTemplate = (template: string, params?: TranslationParams): string => {
    if (!params) return template

    return Object.entries(params).reduce((result, [paramKey, value]) => {
        return result.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value))
    }, template)
}

const getRequiredParamNames = (template: string): string[] => {
    const matches = template.matchAll(/\{([A-Za-z][A-Za-z0-9_]*)(?:,|\})/g)
    return [...new Set(Array.from(matches, ([, paramName]) => paramName))]
}

const hasAllRequiredParams = (template: string, params?: TranslationParams): boolean => {
    const requiredParams = getRequiredParamNames(template)
    if (requiredParams.length === 0) return true
    if (!params) return false

    return requiredParams.every((paramName) => {
        const value = params[paramName]
        return value !== undefined && value !== null
    })
}

export function useTranslation<N extends TranslationNamespace | undefined = undefined>(namespace?: N): UseTranslationReturn {
    const nextT = useNextIntlTranslations(namespace)
    const nextMessages = useMessages()
    const translateNextIntl = nextT as unknown as NextIntlTranslate
    const i18nContext = useI18n()

    const getRawMessage = (key: string): string | undefined => {
        const {fullKey} = resolveTranslationKeys(key, namespace)
        return getMessageAtPath(nextMessages, fullKey) ?? getMessageAtPath(i18nContext.messages, fullKey)
    }

    const translateInternal = (key: string, params?: TranslationParams | string, maybeFallback?: string): string => {
        const fallback = typeof params === 'string' ? params : maybeFallback
        const values = typeof params === 'object' && params !== null ? params as TranslationParams : undefined
        const {relativeKey, fullKey} = resolveTranslationKeys(key, namespace)
        try {
            const translated = translateNextIntl(relativeKey, values)
            if (!isInvalidFromNextIntl(key, translated, namespace)) {
                return translated
            }
        } catch {
            // swallow and fall through
        }

        if (values) {
            return i18nContext.t(fullKey, values, fallback)
        }
        return i18nContext.t(fullKey, fallback)
    }

    const hasTranslation = (key: string): boolean => {
        return Boolean(getRawMessage(key))
    }

    const getTranslationWithFallback = (
        key: string,
        fallback: string,
        params?: TranslationParams
    ): string => {
        const rawMessage = getRawMessage(key)
        if (rawMessage && hasAllRequiredParams(rawMessage, params)) {
            return translateInternal(key, params)
        }

        return interpolateTemplate(fallback, params)
    }

    const formatRelativeTime = (date: Date): string => {
        return i18nContext.dateTime.formatRelative(date)
    }

    return {
        t: translateInternal,

        locale: i18nContext.locale,
        changeLocale: i18nContext.changeLocale,
        isLocaleSupported: i18nContext.isLocaleSupported,
        getLocaleConfig: i18nContext.getLocaleConfig,

        dateTime: i18nContext.dateTime,
        formatNumber: i18nContext.formatNumber,
        formatCurrency: i18nContext.formatCurrency,
        formatRelativeTime,

        hasTranslation,
        getTranslationWithFallback,
    }
}
